// client/src/utils/variance.ts (ST-00298)
//
// The one rule for a route's variance, (received − adjusted dispatch) / adjusted dispatch in %,
// on every page: it exists only when the adjusted dispatch is more than 0.5 t (the server's rule,
// server/utils/variance.ts). Adjusted dispatch is netted (dispatched + BF − CF, on "Truck + time"
// routes also − received off-route − no receipt data); when every dispatch is carried forward it
// nets to floating-point dust, and dividing by that put "+301283365879807872.0%" on a card.
// The rule holds whatever the server sends: an older or cached answer still carries the dust.

/** The adjusted dispatch (t) a variance needs; at or below it there is nothing to compare. */
export const MIN_VARIANCE_BASE_T = 0.5;

/** Why such a route has no variance (cards, tables, tooltips). */
export const NO_VARIANCE_BASE_TEXT =
  'Nothing left dispatched to compare after Brought/Carried Forward';

/** Whether an adjusted dispatch is something to measure a variance against (> 0.5 t). */
export const hasVarianceBase = (adjustedT: number): boolean => adjustedT > MIN_VARIANCE_BASE_T;

/** Receipts (> 0.5 t) stand against no adjusted dispatch (≤ 0.5 t): the variance is n/a. */
export const lacksVarianceBase = (receivedT: number, adjustedT: number): boolean =>
  !hasVarianceBase(adjustedT) && receivedT > MIN_VARIANCE_BASE_T;

/**
 * The variance in %. Without a base: 0 when nothing was received either (an empty route, as
 * before), null = n/a when receipts stand against nothing left dispatched.
 */
export const varianceOf = (receivedT: number, adjustedT: number): number | null =>
  hasVarianceBase(adjustedT)
    ? ((receivedT - adjustedT) / adjustedT) * 100
    : lacksVarianceBase(receivedT, adjustedT)
      ? null
      : 0;
